import apiClient from '../api/client';
import { sha256Bytes } from '../utils/sha256';

const STORAGE_KEYS = {
  accessToken: 'atelier_access_token',
  tenantId: 'atelier_tenant_id',
  tenantSlug: 'atelier_tenant_slug',
  userName: 'atelier_user_name',
  authMode: 'atelier_auth_mode',
} as const;

/**
 * 超管「全部空间」语义哨兵：存入 atelier_tenant_id。
 * 请求头注入时若为 'all' 则跳过 X-Tenant-Id / X-Tenant-Slug（后端对超管无租户头=全部空间）。
 */
export const ALL_TENANTS_SENTINEL = 'all';

const PENDING_OIDC_KEY = 'atelier_oidc_pending';
const REAUTH_ATTEMPTED_KEY = 'atelier_reauth_attempted';

export interface AuthSession {
  accessToken: string;
  tenantId: string;
  tenantSlug: string;
  userName?: string;
  /** 登录通道：facet=统一身份中心（本地 JWT 过期可透明重登），local=本地账号 */
  authMode?: 'local' | 'facet';
}

export interface AuthConfig {
  use_facet_auth: boolean;
  /** 主前端使用的字段名（后端 /auth/config 实际下发） */
  facet_authorization_url?: string;
  /** 契约中写作 facet_authorize_url；后端并行开发中可能二选一下发，读取时互为兜底 */
  facet_authorize_url?: string;
  facet_token_url?: string;
  facet_client_id?: string;
  scopes?: string[];
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  user_id: string;
  tenant_id: string | null;
}

export interface MeResponse {
  id: string;
  email: string;
  display_name: string;
  platform_role: string;
  tenants: Array<{
    tenant_id: string;
    slug: string;
    name: string;
    role: string;
  }>;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.accessToken);
}

export function getAuthSession(): AuthSession | null {
  const accessToken = getAccessToken();
  const tenantId = localStorage.getItem(STORAGE_KEYS.tenantId);
  const tenantSlug = localStorage.getItem(STORAGE_KEYS.tenantSlug);
  if (!accessToken) {
    return null;
  }
  const mode = localStorage.getItem(STORAGE_KEYS.authMode);
  return {
    accessToken,
    tenantId: tenantId || '',
    tenantSlug: tenantSlug || 'default',
    userName: localStorage.getItem(STORAGE_KEYS.userName) || undefined,
    authMode: mode === 'facet' || mode === 'local' ? mode : undefined,
  };
}

export function isLoggedIn(): boolean {
  return !!getAccessToken() && !!localStorage.getItem(STORAGE_KEYS.tenantId);
}

export function saveAuthSession(session: AuthSession): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, session.accessToken);
  localStorage.setItem(STORAGE_KEYS.tenantId, session.tenantId || '');
  localStorage.setItem(STORAGE_KEYS.tenantSlug, session.tenantSlug || 'default');
  if (session.userName) {
    localStorage.setItem(STORAGE_KEYS.userName, session.userName);
  }
  if (session.authMode) {
    localStorage.setItem(STORAGE_KEYS.authMode, session.authMode);
  }
  // 登录成功即清除透明重登标记（允许下次过期再尝试）
  sessionStorage.removeItem(REAUTH_ATTEMPTED_KEY);
}

export function clearAuthSession(): void {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.tenantId);
  localStorage.removeItem(STORAGE_KEYS.tenantSlug);
  localStorage.removeItem(STORAGE_KEYS.userName);
  localStorage.removeItem(STORAGE_KEYS.authMode);
  sessionStorage.removeItem(PENDING_OIDC_KEY);
}

export function getAuthHeaders(): Record<string, string> {
  const session = getAuthSession();
  const headers: Record<string, string> = {};
  if (session?.accessToken) {
    headers['Authorization'] = `Bearer ${session.accessToken}`;
  }
  // 'all' 哨兵=全部空间：不注入租户头（后端对超管无租户头=全部空间）；
  // 同时保持 tenantId 非空，登录判定（isLoggedIn）不受影响。
  if (session?.tenantId && session.tenantId !== ALL_TENANTS_SENTINEL) {
    headers['X-Tenant-Id'] = session.tenantId;
  }
  if (session?.tenantSlug && session.tenantId !== ALL_TENANTS_SENTINEL) {
    headers['X-Tenant-Slug'] = session.tenantSlug;
  }
  return headers;
}

/** 当前生效的租户选择：无记录时默认「全部空间」（超管场景），普通用户登录后必为具体租户。 */
export function getActiveTenantId(): string {
  return localStorage.getItem(STORAGE_KEYS.tenantId) || ALL_TENANTS_SENTINEL;
}

/** 写入顶栏租户切换选择（'all' 或具体租户 id/slug），随后由调用方刷新页面使全站生效。 */
export function setActiveTenant(tenantId: string, tenantSlug: string): void {
  localStorage.setItem(STORAGE_KEYS.tenantId, tenantId);
  localStorage.setItem(STORAGE_KEYS.tenantSlug, tenantSlug);
}

// ------------------------------------------------------------------
// Facet OIDC helpers
// ------------------------------------------------------------------

function base64urlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += chars[randomValues[i] % chars.length];
  }
  return result;
}

export function generateCodeVerifier(): string {
  return randomString(128);
}

export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  // crypto.subtle 仅在安全上下文（HTTPS/localhost）可用；
  // 通过 http://<IP>:<port> 访问时回退到纯 JS 实现，避免登录页报错。
  if (globalThis.crypto?.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64urlEncode(digest);
  }
  return base64urlEncode(sha256Bytes(data).buffer as ArrayBuffer);
}

export function generateState(): string {
  return randomString(32);
}

export interface OidcPending {
  codeVerifier: string;
  state: string;
  redirectUri: string;
}

export function saveOidcPending(pending: OidcPending): void {
  sessionStorage.setItem(PENDING_OIDC_KEY, JSON.stringify(pending));
}

export function getOidcPending(): OidcPending | null {
  const raw = sessionStorage.getItem(PENDING_OIDC_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OidcPending;
  } catch {
    return null;
  }
}

export function clearOidcPending(): void {
  sessionStorage.removeItem(PENDING_OIDC_KEY);
}

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const res = await apiClient.get<AuthConfig>('/auth/config');
  return res.data;
}

export function buildFacetAuthUrl(
  config: AuthConfig,
  redirectUri: string,
  codeChallenge: string,
  state: string
): string {
  const url = new URL(config.facet_authorization_url || config.facet_authorize_url || '');
  url.searchParams.set('client_id', config.facet_client_id || '');
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  url.searchParams.set('scope', (config.scopes || ['openid', 'profile', 'email']).join(' '));
  return url.toString();
}

export async function startFacetLogin(config: AuthConfig): Promise<void> {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const codeVerifier = generateCodeVerifier();
  const state = generateState();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  saveOidcPending({ codeVerifier, state, redirectUri });
  window.location.href = buildFacetAuthUrl(config, redirectUri, codeChallenge, state);
}

export async function exchangeFacetCode(code: string, state: string): Promise<AuthSession> {
  const pending = getOidcPending();
  if (!pending) {
    throw new Error('未找到待完成的 OIDC 登录会话，请重新登录');
  }
  if (pending.state !== state) {
    throw new Error('OIDC state 参数不匹配');
  }
  const tokenRes = await apiClient.post<TokenResponse>('/auth/callback', {
    code,
    redirect_uri: pending.redirectUri,
    code_verifier: pending.codeVerifier,
    state,
  });
  clearOidcPending();
  return buildSessionFromToken(tokenRes.data, 'facet');
}

/** 本地账号兜底登录：POST /auth/token 换 token，再解析默认租户并落会话（authMode=local）。 */
export async function localLogin(email: string, password: string): Promise<AuthSession> {
  const tokenRes = await apiClient.post<TokenResponse>('/auth/token', {
    email,
    password,
  });
  return buildSessionFromToken(tokenRes.data, 'local', email);
}

/** 从 token 响应构建会话：尝试用 /auth/me 解析默认租户 slug/name，失败则回退默认值。 */
async function buildSessionFromToken(
  token: TokenResponse,
  authMode: 'local' | 'facet',
  fallbackName?: string
): Promise<AuthSession> {
  const { access_token, tenant_id } = token;
  let tenantSlug = 'default';
  let tenantName = '';
  let userName = '';
  try {
    const meRes = await apiClient.get<MeResponse>('/auth/me', {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    userName = meRes.data.display_name || meRes.data.email;
    const defaultTenant =
      meRes.data.tenants.find((t) => t.tenant_id === tenant_id) || meRes.data.tenants[0];
    if (defaultTenant) {
      tenantSlug = defaultTenant.slug;
      tenantName = defaultTenant.name;
    }
  } catch {
    // /auth/me 不可用时回退默认租户，不阻断登录
  }

  const session: AuthSession = {
    accessToken: access_token,
    tenantId: tenant_id || '',
    tenantSlug,
    userName: userName || tenantName || fallbackName || '',
    authMode,
  };
  saveAuthSession(session);
  return session;
}

// ------------------------------------------------------------------
// 401 透明重登：本地会话 JWT（24h）过期后，若走 Facet 登录且 SSO 会话
// 仍在，直接重走授权流无感换新 token，而不是把用户甩到登录页。
// 用 sessionStorage 标记防循环：重登回来仍 401 才清会话去登录页。
// ------------------------------------------------------------------

export function canAttemptSilentReauth(): boolean {
  const session = getAuthSession();
  return session?.authMode === 'facet' && !sessionStorage.getItem(REAUTH_ATTEMPTED_KEY);
}

export async function attemptSilentFacetReauth(): Promise<boolean> {
  if (!canAttemptSilentReauth()) return false;
  sessionStorage.setItem(REAUTH_ATTEMPTED_KEY, '1');
  try {
    const config = await fetchAuthConfig();
    if (!config.use_facet_auth) return false;
    await startFacetLogin(config); // 整页跳转，正常不会返回
    return true;
  } catch {
    return false;
  }
}
